import { Controller } from '@hotwired/stimulus'
import * as Sentry from '@sentry/browser'

import { VGSError } from '../errors'
// VGSShow comes from https://js.verygoodvault.com/vgs-show/2.0.0/show.js
// which is loaded via `javascript_include_tag`, which makes it available as
// `window.VGSShow`.
declare global {
  interface Window {
    VGSShow: {
      create(vaultId: string, stateCallback?: (state: any) => void): any;
    };
  }
}

const copyIcon = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" display="block"> <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 8.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v8.25A2.25 2.25 0 006 16.5h2.25m8.25-8.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-7.5A2.25 2.25 0 018.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 00-2.25 2.25v6" /></svg>'

// Connects to data-controller="vgs-show"
export default class extends Controller {
  cardNumberIframeReady = false
  cvv2IframeReady = false
  firstReveal = true
  show: any

  static targets = [
    'copyNumberButton',
    'hideLink',
    'maskedCardNumber',
    'maskedCvv2',
    'secretCardNumber',
    'secretCvv2',
  ]

  declare readonly copyNumberButtonTarget: HTMLElement
  declare readonly hideLinkTarget: HTMLElement
  declare readonly maskedCardNumberTarget: HTMLElement
  declare readonly maskedCvv2Target: HTMLElement
  declare readonly secretCardNumberTarget: HTMLElement
  declare readonly secretCvv2Target: HTMLElement

  static values = {
    cardId: String,
    customerToken: String,
    vgsVaultId: String,
  }

  declare cardIdValue: string
  declare customerTokenValue: string
  declare vgsVaultIdValue: string

  connect (): void {
    this.show = window.VGSShow.create(this.vgsVaultIdValue)
    this.#createIframes()
  }

  #createIframes (): void {
    const cardNumberIframe = this.show.request({
      name: 'data-text',
      method: 'GET',
      path: '/cards/' + this.cardIdValue + '/secure-data/pan',
      headers: {
        Authorization: 'Bearer ' + this.customerTokenValue,
      },
      htmlWrapper: 'text',
      jsonPathSelector: 'data.attributes.pan',
      serializers: [this.show.SERIALIZERS.replace('(\\d{4})(\\d{4})(\\d{4})(\\d{4})', '$1 $2 $3 $4')],
    })

    cardNumberIframe.on('revealSuccess', () => {
      this.maskedCardNumberTarget.classList.add('hidden')
      this.secretCardNumberTarget.classList.remove('hidden')
      this.copyNumberButtonTarget.classList.remove('hidden')
    })

    cardNumberIframe.on('requestFail', (error: any) => {
      Sentry.addBreadcrumb({
        category: 'VGS Show',
        message: 'requestFail',
        data: {
          cardId: this.cardIdValue,
          frame: 'cardNumber',
        },
      })
      throw new VGSError(error.message)
    })

    cardNumberIframe.on('revealFail', () => {
      Sentry.addBreadcrumb({
        category: 'VGS Show',
        message: 'revealFail',
        data: {
          cardId: this.cardIdValue,
          frame: 'cardNumber',
        },
      })
      throw new VGSError('Card number reveal failed')
    })

    cardNumberIframe.render('#secret-card-number', {
      'box-sizing': 'border-box',
      color: 'black',
      display: 'block',
      'font-family': 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      'line-height': '1.25rem',
      'tab-size': '4',
      'text-size-adjust': 'none',
      'font-size': '.875rem',
    })

    const copyNumberButton = this.show.copyFrom(cardNumberIframe, { text: copyIcon })

    copyNumberButton.render('#copy-number-button', {
      cursor: 'pointer',
      width: '1.25rem',
      color: '#94a3b8',
      display: 'block',
      '&:hover': {
        color: '#4f46e5',
      },
      '&:active': {
        color: '#d946ef',
      },
    })

    const cvv2iframe = this.show.request({
      name: 'data-text',
      method: 'GET',
      path: '/cards/' + this.cardIdValue + '/secure-data/cvv2',
      headers: {
        Authorization: 'Bearer ' + this.customerTokenValue,
      },
      htmlWrapper: 'text',
      jsonPathSelector: 'data.attributes.cvv2',
    })

    cvv2iframe.on('revealSuccess', () => {
      this.maskedCvv2Target.classList.add('hidden')
      this.secretCvv2Target.classList.remove('hidden')
    })

    cvv2iframe.on('requestFail', (error: any) => {
      Sentry.addBreadcrumb({
        category: 'VGS Show',
        message: 'requestFail',
        data: {
          cardId: this.cardIdValue,
          frame: 'cvv2',
        },
      })
      throw new VGSError(error.message)
    })

    cvv2iframe.on('revealFail', () => {
      Sentry.addBreadcrumb({
        category: 'VGS Show',
        message: 'revealFail',
        data: {
          cardId: this.cardIdValue,
          frame: 'cvv2',
        },
      })
      throw new VGSError('CVV2 reveal failed')
    })

    cvv2iframe.render('#secret-cvv2', {
      'box-sizing': 'border-box',
      color: 'black',
      display: 'block',
      'font-family': 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      'line-height': '1.25rem',
      'tab-size': '4',
      'text-size-adjust': 'none',
      'font-size': '0.875rem',
    })
  }
}
